import React, { useMemo, useState } from 'react';
import styled from 'astroturf/react';

import { Heading } from 'js/design_system/Heading';
import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { Banner } from 'js/design_system/Banner';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { Input } from 'js/design_system/forms/Input';
import { Checkbox } from 'js/design_system/forms/Checkbox';
import routes from 'js/core_pre_webpack/routes';
import { fetchWithCsrf } from 'js/utils/fetch_with_csrf';

export type SubscriptionJournal = {
  id: number;
  name: string;
  subscribed: boolean;
  blockedReason?: string | null;
};

// The journal whose page this is. Absent on the all-journals page at /journals/subscribe.
export type FocusedJournal = {
  id: number;
  name: string;
};

export type RelatedJournal = {
  id: number;
  name: string;
};

// How a visit that followed a confirmation link went. That action redirects here rather than
// rendering, so the outcome travels in the URL — and this mirrors the closed set the server
// echoes back, so no text a visitor put in the URL is ever rendered on the page.
export type ConfirmationOutcome =
  | 'confirmed'
  | 'unusable'
  | 'blocked'
  | 'failed';

// A union rather than optional fields: only a visitor the server could name has preferences
// to describe, so accountBlockReason is meaningless on the anonymous branch. Modelling it this
// way means an anonymous branch carrying one cannot be constructed.
export type JournalSubscriptionPageProps = {
  token: string | null;
  journal: FocusedJournal | null;
  relatedJournals: RelatedJournal[];
  subscribePath: string;
  allJournalsPath: string;
  // The account-wide email settings tab, or null for a visitor with no account to open it —
  // which is most of this page's audience, since the email CTA mails offsite authors.
  notificationsPath: string | null;
  // Extra text the all-journals search box matches on, keyed by journal id — the journal's
  // section names. Null on a journal page, which lists four journals and has no search box.
  searchKeywords: Record<number, string[]> | null;
  // What the picker lists. Sent to both branches, because signing up is a choice of journals
  // rather than a choice to take all of them — an anonymous visitor gets the same list, search
  // box and select-all. Their rows arrive unticked and uncaptioned: the server has no address
  // to describe. Empty on a journal page, which offers its related journals instead.
  journals: SubscriptionJournal[];
  // Null on an ordinary visit; set only when the visitor arrived from a confirmation link.
  confirmation: ConfirmationOutcome | null;
  // Set only on the page a confirmation link lands on. That page writes nothing, so the token
  // has to come back out here for the click to post.
  pendingConfirmation: {
    token: string;
    confirmPath: string;
    journalNames: string[];
  } | null;
} & (
  | {
      identified: true;
      // Suppression that applies to the whole account rather than to one journal, so it is
      // stated once above the list. Repeating it per row captions every journal identically.
      accountBlockReason?: string | null;
      // The settings tab, but only when it carries a control for whatever is in
      // accountBlockReason and only for a visitor who has an account to open it. Null means the
      // banner offers support, which for the remaining blocks is the only route there is.
      accountBlockSettingsPath?: string | null;
    }
  | { identified: false }
);

const Page = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 48px 24px 96px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const SignupForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  border: 1px solid var(--stroke-light);
  border-radius: 8px;
`;

const JournalList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const JournalRow = styled.li`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid var(--stroke-light);
`;

const JournalLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const BlockedNote = styled.span`
  color: var(--text-secondary);
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AllJournalsRow = styled.div`
  padding: 16px;
  border-radius: 8px;
  background: var(--background-grey);
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Footer = styled.div`
  @import 'stylesheets/design_system/font';
  @include body__small;

  padding-top: 8px;
  border-top: 1px solid var(--stroke-light);
`;

// Banner lays its children out with `display: flex`, so prose passed as several nodes — a
// sentence, a link, the rest of the sentence — becomes that many columns with gaps between
// them rather than one wrapped paragraph. Anything with a link in it goes through here.
const BannerText = styled.span`
  display: block;
`;

const EmptyResult = styled.div`
  padding: 24px 0;
  color: var(--text-secondary);
`;

// fetchWithCsrf rather than the api_utils helpers: CLAUDE.md names it as the convention, and
// api_utils' getCsrfToken() resolves its promise only if it finds a token -- with no csrf-token
// meta tag on the page it neither resolves nor rejects, leaving the button on "Subscribing..."
// for good. fetchWithCsrf rejects instead. It also resolves for non-2xx, so rethrow the Response
// to keep the rejection shape serverError() reads.
async function sendJson(
  path: string,
  method: 'POST' | 'PATCH',
  body: unknown,
): Promise<unknown> {
  const response = await fetchWithCsrf(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw response;
  }
  return response.json();
}

/**
 * The rejection carries the `Response` itself, so the server's `{error: …}` body is available.
 * Without this every failure collapsed into one generic sentence, hiding the messages the
 * endpoint goes to the trouble of writing — "Enter a valid email address", and the rate-limit
 * notice telling the visitor to wait rather than to retry immediately. A missing CSRF token
 * rejects with a bare string, which is not a Response, so it lands on the fallback.
 */
async function serverError(
  rejection: unknown,
  fallback: string,
): Promise<string> {
  if (rejection instanceof Response) {
    try {
      const body = (await rejection.json()) as { error?: string };
      if (body?.error) {
        return body.error;
      }
    } catch {
      // A non-JSON body (a proxy error page, say); the generic message is the best we have.
    }
  }
  return fallback;
}

/**
 * Matches a journal against what the visitor typed, on its name and on its section names.
 *
 * Section names carry the search because the names alone do not: someone looking for Academia
 * Oncology types "cancer", and someone looking for Endocrinology and Metabolism types
 * "diabetes". Every term has to match something, so "cancer imaging" narrows rather than
 * widens, but the terms may land in different fields — that is the query above, where "cancer"
 * matches a section of Medical Imaging and "imaging" matches its name.
 */
function matchesQuery(
  journal: SubscriptionJournal,
  keywords: string[],
  query: string,
): boolean {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) {
    return true;
  }
  const haystack = [journal.name, ...keywords].join(' ').toLowerCase();
  return terms.every((term) => haystack.includes(term));
}

/**
 * The all-journals chooser: select-all, search box, and a checkbox per journal.
 *
 * Shared by both branches because the two pages ask the same question. The anonymous form used
 * to be an address field alone, with an empty submit meaning every journal — so the page that
 * exists to let someone choose offered no choice, and the commonest accident produced the
 * widest enrolment.
 *
 * Owns the query because nothing outside it reads what was typed. Searching narrows what is
 * drawn and never what a submit sends: the callers pass the full list, so typing before
 * clicking cannot silently shrink the selection.
 */
function JournalPicker({
  journals,
  settings,
  onChange,
  searchKeywords,
  heading,
  disabled = false,
}: {
  journals: SubscriptionJournal[];
  settings: Record<number, boolean>;
  onChange: (next: Record<number, boolean>) => void;
  searchKeywords: Record<number, string[]> | null;
  heading: string;
  // Leaves the search box alone: narrowing what is drawn changes nothing that gets saved, so
  // it stays usable for reading the list even when nothing on it can be picked.
  disabled?: boolean;
}) {
  const [query, setQuery] = useState('');

  // every() is true for an empty list, which would render the select-all box ticked on a page
  // offering nothing.
  const allChecked =
    journals.length > 0 &&
    journals.every((journalRow) => settings[journalRow.id]);

  const visibleJournals = useMemo(
    () =>
      journals.filter((journalRow) =>
        matchesQuery(journalRow, searchKeywords?.[journalRow.id] ?? [], query),
      ),
    [journals, searchKeywords, query],
  );

  return (
    <>
      <AllJournalsRow>
        <Checkbox
          checked={allChecked}
          onChange={(checked) =>
            onChange(
              Object.fromEntries(
                journals.map((journalRow) => [journalRow.id, checked]),
              ),
            )
          }
          label="Every Academia.edu Journal"
          disabled={disabled}
        />
      </AllJournalsRow>

      <Section>
        <Heading size="sm" as="h2">
          {heading}
        </Heading>
        <Input
          type="search"
          label="Search journals"
          placeholder="Search by name or subject, e.g. cancer"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <JournalList>
          {visibleJournals.map((journalRow) => (
            <JournalRow key={journalRow.id}>
              <JournalLabel>
                <Checkbox
                  checked={Boolean(settings[journalRow.id])}
                  onChange={(checked) =>
                    onChange({ ...settings, [journalRow.id]: checked })
                  }
                  label={journalRow.name}
                  disabled={disabled}
                />
                {journalRow.blockedReason && (
                  <Body size="sm">
                    <BlockedNote>{journalRow.blockedReason}</BlockedNote>
                  </Body>
                )}
              </JournalLabel>
            </JournalRow>
          ))}
        </JournalList>
        {/* Conditioned on the query, not just on the list being empty: an empty list with an
            empty box is a page with no journals to offer, which this sentence would misreport
            as a search that found nothing. */}
        {visibleJournals.length === 0 && query.trim() !== '' && (
          <EmptyResult>
            <Body size="sm">No journals match “{query}”.</Body>
          </EmptyResult>
        )}
      </Section>
    </>
  );
}

function SignupSection({
  subscribePath,
  journal,
  relatedJournals,
  journals,
  searchKeywords,
  onConfirmationSent,
}: {
  subscribePath: string;
  journal: FocusedJournal | null;
  relatedJournals: RelatedJournal[];
  journals: SubscriptionJournal[];
  searchKeywords: Record<number, string[]> | null;
  onConfirmationSent: () => void;
}) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Separate from error, which is bound to the email field. An empty selection is not a fault
  // in the address, and reporting it there draws a red border round a perfectly good email.
  const [journalError, setJournalError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Unticked by default on both pages. On a journal page the visitor came about one journal,
  // so that journal is the only thing their submit can fairly be read as asking for, and
  // pre-ticking four more would quadruple what one click of the confirmation link enrols an
  // address into. On the all-journals page the whole point is that taking all of them is a
  // choice rather than the default, so the select-all starts off and an empty submit is
  // refused below.
  const [alsoSubscribe, setAlsoSubscribe] = useState<Record<number, boolean>>(
    {},
  );

  const chosenIds = journals
    .map((journalRow) => journalRow.id)
    .filter((id) => alsoSubscribe[id]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    // Checked here as well as on the server so the commonest mistake on a 29-box form answers
    // instantly and without a round trip that carries the address along with it.
    if (!journal && chosenIds.length === 0) {
      setJournalError('Choose at least one journal');
      return;
    }
    setJournalError(null);
    setError(null);
    setSaving(true);
    try {
      // On a journal page the server adds the focused journal itself, so this carries the
      // journal plus whichever related boxes were ticked. On the all-journals page it carries
      // exactly what was chosen: the server no longer reads an absent list as "all of them".
      const journalIds = journal
        ? [
            journal.id,
            ...relatedJournals
              .map((r) => r.id)
              .filter((id) => alsoSubscribe[id]),
          ]
        : chosenIds;
      const response = (await sendJson(subscribePath, 'POST', {
        email,
        journal_ids: journalIds,
      })) as { loginUrl?: string };
      // A registered address is not subscribed from here: the server sends us to sign in, and
      // brings us back to this page identified so the journals get chosen rather than assumed.
      if (response.loginUrl) {
        window.location.href = response.loginUrl;
        return;
      }
      onConfirmationSent();
    } catch (rejection) {
      setError(
        await serverError(
          rejection,
          'We could not sign you up just now. Please try again.',
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <SignupForm onSubmit={submit}>
      <Input
        label="Email address"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        errors={error ? [error] : undefined}
        hasError={Boolean(error)}
      />
      {!journal && (
        <JournalPicker
          journals={journals}
          settings={alsoSubscribe}
          onChange={(next) => {
            setJournalError(null);
            setAlsoSubscribe(next);
          }}
          searchKeywords={searchKeywords}
          heading="Choose your journals"
        />
      )}
      {journal && relatedJournals.length > 0 && (
        <Section>
          <Heading size="xs" as="h2">
            Also send me research from
          </Heading>
          <JournalList>
            {relatedJournals.map((related) => (
              <JournalRow key={related.id}>
                <Checkbox
                  checked={Boolean(alsoSubscribe[related.id])}
                  onChange={(checked) =>
                    setAlsoSubscribe((prev) => ({
                      ...prev,
                      [related.id]: checked,
                    }))
                  }
                  label={related.name}
                />
              </JournalRow>
            ))}
          </JournalList>
        </Section>
      )}
      {journalError && <Banner type="danger">{journalError}</Banner>}
      <Actions>
        <Button type="submit" disabled={saving}>
          {saving ? 'Subscribing…' : 'Subscribe'}
        </Button>
      </Actions>
    </SignupForm>
  );
}

function PreferencesSection({
  journals: initialJournals,
  accountBlockReason: initialAccountBlockReason,
  accountBlockSettingsPath: initialAccountBlockSettingsPath,
  token,
  journal,
  relatedJournals,
  searchKeywords,
}: {
  journals: SubscriptionJournal[];
  accountBlockReason?: string | null;
  accountBlockSettingsPath?: string | null;
  token: string | null;
  journal: FocusedJournal | null;
  relatedJournals: RelatedJournal[];
  searchKeywords: Record<number, string[]> | null;
}) {
  const [journals, setJournals] =
    useState<SubscriptionJournal[]>(initialJournals);
  const [accountBlockReason, setAccountBlockReason] = useState<
    string | null | undefined
  >(initialAccountBlockReason);
  const [accountBlockSettingsPath, setAccountBlockSettingsPath] = useState<
    string | null | undefined
  >(initialAccountBlockSettingsPath);
  const [settings, setSettings] = useState<Record<number, boolean>>(
    Object.fromEntries(initialJournals.map((j) => [j.id, j.subscribed])),
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Names the journal a confirm click just subscribed to, so the banner can say what happened
  // rather than "preferences saved" -- which is true of a first subscription but describes it
  // as an edit to settings the visitor had never seen.
  const [subscribedName, setSubscribedName] = useState<string | null>(null);

  // What this page manages. The server sends every journal either way, because the save path's
  // allowlist and the block reasons are computed over the whole set — but a journal page is
  // about its own journal and its neighbours, so it edits only those and links to the
  // all-journals page for the rest. Anything outside this list is left untouched by a save
  // rather than being sent back at its current value, so a stale tab cannot revert a change
  // made elsewhere to a journal it was not even showing.
  //
  // Ordered deliberately rather than inheriting the server's alphabetical sort. On a journal
  // page that sort buries the journal the page is about somewhere inside its own list of
  // suggestions — Oncology lands last of its five. The focused journal leads; the rest follow
  // in the curated order, which is most-related first.
  const byId = useMemo(
    () => new Map(journals.map((j) => [j.id, j])),
    [journals],
  );

  const focusedRow = journal ? byId.get(journal.id) : undefined;

  // Keyed on what the server holds rather than on the checkbox, because it chooses which
  // control the page offers rather than what a save would write. Someone arriving from an
  // announcement email came to subscribe to this one journal, so that is the single primary
  // action; the checkbox appears once there is a subscription to untick.
  const needsConfirm = Boolean(focusedRow && !focusedRow.subscribed);

  const relatedRows = useMemo(
    () =>
      relatedJournals
        .map((related) => byId.get(related.id))
        .filter((row): row is SubscriptionJournal => row !== undefined),
    [relatedJournals, byId],
  );

  const scopedJournals = useMemo(() => {
    if (!journal) {
      return journals;
    }
    return focusedRow ? [focusedRow, ...relatedRows] : relatedRows;
  }, [journals, journal, focusedRow, relatedRows]);

  // Both banners describe what the server holds, so any edit makes them stale. Clearing them on
  // the first change stops either sitting beside checkboxes that have since moved -- in the
  // error case, stops a failed save's message reading as a verdict on the current selection.
  const changeSettings = (
    update: React.SetStateAction<Record<number, boolean>>,
  ) => {
    setSaved(false);
    setError(null);
    setSubscribedName(null);
    setSettings(update);
  };

  // Subscribing does not by itself guarantee delivery: a staff or one-click unsubscribe, a
  // global opt-out or the publishing_distribution flag each suppress independently, and none
  // of them is cleared by ticking a box here. Saying "saved" alone would report success for
  // mail that will not arrive.
  const blockedByJournal = scopedJournals.filter(
    (journalRow) => settings[journalRow.id] && journalRow.blockedReason,
  );
  // The account-level layers suppress every journal at once, so a single ticked journal is
  // enough for "saved" on its own to be a false promise.
  const anyTicked = scopedJournals.some(
    (journalRow) => settings[journalRow.id],
  );
  const stillBlocked =
    blockedByJournal.length > 0 || Boolean(accountBlockReason && anyTicked);

  // Every journal is suppressed while an account-level block stands, so every control below is
  // inert: a tick records a preference nothing can act on, and a save answering "saved" over a
  // banner saying nothing will be sent reads as the save having fixed it. Disabled rather than
  // hidden, so the page still shows what it would offer once the block is lifted -- which is
  // what the banner above links to.
  const blockedByAccount = Boolean(accountBlockReason);

  const renderRow = (journalRow: SubscriptionJournal) => (
    <JournalRow key={journalRow.id}>
      <JournalLabel>
        <Checkbox
          checked={Boolean(settings[journalRow.id])}
          onChange={(checked) =>
            changeSettings((prev) => ({ ...prev, [journalRow.id]: checked }))
          }
          label={journalRow.name}
          disabled={blockedByAccount}
        />
        {journalRow.blockedReason && (
          <Body size="sm">
            <BlockedNote>{journalRow.blockedReason}</BlockedNote>
          </Body>
        )}
      </JournalLabel>
    </JournalRow>
  );

  const persist = async (scoped: Record<number, boolean>) => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      // The same endpoint from both pages: saving is journal-agnostic, the body carries which
      // journals are being set, and the journal page simply sends fewer of them.
      const response = (await sendJson(
        routes.academia_journals_subscribe_preferences_path(),
        'PATCH',
        { prefs_token: token, journal_settings: scoped },
      )) as {
        journals: SubscriptionJournal[];
        accountBlockReason?: string | null;
        accountBlockSettingsPath?: string | null;
      };
      // Resync all of it from the server's answer. Updating only journals would leave the
      // checkboxes showing what was asked for while the captions describe what was recorded,
      // and keeping the old settings path would offer a route out of a block that has since
      // been replaced by one the settings tab cannot lift.
      setJournals(response.journals);
      setSettings(
        Object.fromEntries(response.journals.map((j) => [j.id, j.subscribed])),
      );
      setAccountBlockReason(response.accountBlockReason);
      setAccountBlockSettingsPath(response.accountBlockSettingsPath);
      setSaved(true);
      return true;
    } catch (rejection) {
      setError(
        await serverError(
          rejection,
          'We could not save your preferences. Please try again.',
        ),
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const save = () =>
    persist(
      Object.fromEntries(
        scopedJournals.map((j) => [j.id, Boolean(settings[j.id])]),
      ),
    );

  // The second click of the two-click subscribe the announcement emails link into. It writes
  // the journal the email was about, and carries the related boxes along at whatever the
  // visitor left them at, so arriving, ticking a neighbour and confirming does not quietly
  // discard the tick.
  const confirmSubscribe = async () => {
    if (!focusedRow) {
      return;
    }
    const ok = await persist({
      ...Object.fromEntries(
        scopedJournals.map((j) => [j.id, Boolean(settings[j.id])]),
      ),
      [focusedRow.id]: true,
    });
    if (ok) {
      setSubscribedName(focusedRow.name);
    }
  };

  return (
    <>
      {/*
        An account-level block is the one state this page cannot clear itself: subscribing here
        will not lift a global opt-out, a wildcard unsubscribe, or an email-settings flag, so
        without somewhere to go the identified branch is a dead end, and less helpful than the
        anonymous one. Where the settings tab carries the control the banner sends them there;
        the rest fall back to the same support link the signup banner uses.
      */}
      {accountBlockReason && (
        <Banner type="warning">
          <BannerText>
            {accountBlockReason}. In order to subscribe to our journals, you
            must first opt back in to allow email notifications.{' '}
            {accountBlockSettingsPath ? (
              <>
                Control your{' '}
                <BodyLink href={accountBlockSettingsPath} underline>
                  Notification Settings
                </BodyLink>
              </>
            ) : (
              <BodyLink
                href="https://support.academia.edu/hc/en-us/requests/new"
                underline
              >
                Contact support
              </BodyLink>
            )}
            .
          </BannerText>
        </Banner>
      )}

      {/*
        Two sections on a journal page rather than one list of five. Merged, the journal the
        page is about is just another checkbox among its own suggestions, and "Your journals"
        describes the two groups equally badly — one is what the visitor came for, the other is
        what we are proposing.
      */}
      {journal ? (
        <>
          <Section>
            <Heading size="sm" as="h2">
              This journal
            </Heading>
            {/* No checkbox while unsubscribed: the button below is the whole decision, and a
                tickbox beside it would be a second control for the same thing that the button
                then appears to ignore. */}
            <JournalList>
              {focusedRow &&
                (needsConfirm ? (
                  <JournalRow>
                    <Body size="sm">
                      You&apos;re not subscribed to {focusedRow.name} yet.
                    </Body>
                  </JournalRow>
                ) : (
                  renderRow(focusedRow)
                ))}
            </JournalList>
          </Section>

          {relatedRows.length > 0 && (
            <Section>
              <Heading size="sm" as="h2">
                Related journals
              </Heading>
              <JournalList>{relatedRows.map(renderRow)}</JournalList>
            </Section>
          )}
        </>
      ) : (
        <JournalPicker
          journals={scopedJournals}
          settings={settings}
          onChange={changeSettings}
          searchKeywords={searchKeywords}
          heading="Choose your journals"
          disabled={blockedByAccount}
        />
      )}

      {error && <Banner type="danger">{error}</Banner>}
      {saved &&
        (stillBlocked ? (
          <Banner type="warning">
            <BannerText>
              Your preferences are saved, but nothing will be sent
              {blockedByJournal.length > 0
                ? ` for ${blockedByJournal
                    .map(
                      (journalRow) =>
                        `${journalRow.name} (${journalRow.blockedReason})`,
                    )
                    .join(', ')}`
                : ''}
              . Contact support if you did not expect this.
            </BannerText>
          </Banner>
        ) : (
          <Banner type="success">
            {subscribedName
              ? `You're subscribed to ${subscribedName}. We'll email you as new research is published.`
              : 'Your preferences are saved.'}
          </Banner>
        ))}

      <Actions>
        {needsConfirm && focusedRow ? (
          <Button
            onClick={confirmSubscribe}
            disabled={saving || blockedByAccount}
          >
            {saving ? 'Subscribing…' : `Subscribe to ${focusedRow.name}`}
          </Button>
        ) : (
          <Button onClick={save} disabled={saving || blockedByAccount}>
            {saving ? 'Saving…' : 'Save preferences'}
          </Button>
        )}
      </Actions>
    </>
  );
}

// What following a confirmation link did. Unlike the signup form's response, this one can name
// the reason: whoever is reading it followed a link out of the inbox the mail was sent to, so
// there is no third party whose preferences saying so would disclose.
function ConfirmationBanner({ outcome }: { outcome: ConfirmationOutcome }) {
  switch (outcome) {
    case 'confirmed':
      return (
        <Banner type="success">
          You&apos;re subscribed. We&apos;ll email you as new research is
          published.
        </Banner>
      );
    case 'blocked':
      return (
        <Banner type="warning">
          <BannerText>
            That address has unsubscribed from Academia email, so we have not
            subscribed it.{' '}
            <BodyLink
              href="https://support.academia.edu/hc/en-us/requests/new"
              underline
            >
              Contact support
            </BodyLink>{' '}
            to turn it back on.
          </BannerText>
        </Banner>
      );
    case 'unusable':
      return (
        <Banner type="warning">
          That confirmation link has expired or is no longer valid. Enter your
          email address below and we&apos;ll send a fresh one.
        </Banner>
      );
    case 'failed':
      return (
        <Banner type="warning">
          We could not finish setting up your subscription just now. Please
          enter your email address below to try again.
        </Banner>
      );
  }
}

// Mirrors Letters::JournalSubscriptions::ConfirmationToken::PARAM, which is the key the
// controller reads the token back out of.
const CONFIRMATION_TOKEN_PARAM = 'confirmation_token';

// The second click of the confirmation. The link out of the inbox only got the visitor here;
// nothing is written until this button is pressed, so a scanner or a prefetching client that
// followed the link cannot complete a signup on someone's behalf.
function ConfirmSubscriptionSection({
  pending,
}: {
  pending: { token: string; confirmPath: string; journalNames: string[] };
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = (await sendJson(pending.confirmPath, 'POST', {
        [CONFIRMATION_TOKEN_PARAM]: pending.token,
      })) as { redirectTo: string };
      // Every outcome answers with somewhere to go, including the ones that decline: the
      // server decides what a link it can no longer honour should show.
      window.location.href = response.redirectTo;
    } catch (rejection) {
      setError(
        await serverError(
          rejection,
          'We could not confirm your subscription just now. Please try again.',
        ),
      );
      setSaving(false);
    }
  };

  return (
    <Section>
      <Heading size="sm" as="h2">
        Confirm your subscription
      </Heading>
      <Body size="md">
        You asked to hear about new research in{' '}
        {pending.journalNames.join(', ')}. Nothing is sent until you confirm.
      </Body>
      {error && <Banner type="danger">{error}</Banner>}
      <Actions>
        <Button onClick={confirm} disabled={saving}>
          {saving ? 'Confirming…' : 'Confirm subscription'}
        </Button>
      </Actions>
    </Section>
  );
}

export default function JournalSubscriptionPage(
  props: JournalSubscriptionPageProps,
) {
  // Submitting the form does not subscribe anyone any more — it sends a confirmation link and
  // nothing else — so this tracks only that the link is on its way.
  const [confirmationSent, setConfirmationSent] = useState(false);
  const { journal, relatedJournals } = props;

  // Signed-in visitors get the settings tab covering every Academia email. Nobody else has one:
  // the email CTA mails offsite authors, who are named by a token and have no account, and for
  // them the all-journals view is the whole of what there is to manage — so it wears the same
  // label. On that page already, there is nothing left to point at, so the link is dropped.
  const footerPath =
    props.notificationsPath ?? (journal ? props.allJournalsPath : null);

  return (
    <Page>
      <Intro>
        {/*
          Serif, like every other h1 across the Journals site — the microsite heroes, the
          about pages, the article pages. The sans-serif default marked this page as belonging
          to Academia.edu rather than to the journal the visitor had just come from.
        */}
        <Heading fontFamily="serif" size="xl" as="h1">
          {journal
            ? `Get research updates from ${journal.name}`
            : 'Get research updates from Academia.edu Journals'}
        </Heading>
        <Body size="lg">
          We&rsquo;ll notify you about new research when it&rsquo;s published
        </Body>
      </Intro>

      {/*
        A token that arrived but did not resolve is expired, tampered with, or names a row
        that has since been deleted. Without this the page just renders the signup form, and
        someone following a link from an older email is left to conclude their preferences
        vanished.
      */}
      {!props.identified && props.token && !confirmationSent && (
        <Banner type="warning">
          <BannerText>
            That preferences link has expired. Every email we send carries an
            unsubscribe link at the bottom, or{' '}
            <BodyLink
              href="https://support.academia.edu/hc/en-us/requests/new"
              underline
            >
              contact support
            </BodyLink>{' '}
            to change what you receive.
          </BannerText>
        </Banner>
      )}

      {props.confirmation && (
        <ConfirmationBanner outcome={props.confirmation} />
      )}

      {props.pendingConfirmation ? (
        <ConfirmSubscriptionSection pending={props.pendingConfirmation} />
      ) : props.identified ? (
        <PreferencesSection
          journals={props.journals}
          accountBlockReason={props.accountBlockReason}
          accountBlockSettingsPath={props.accountBlockSettingsPath}
          token={props.token}
          journal={journal}
          relatedJournals={relatedJournals}
          searchKeywords={props.searchKeywords}
        />
      ) : confirmationSent ? (
        /*
          Careful not to promise the subscription itself. Submitting sends a confirmation link
          and writes nothing, so the only thing true at this point is that the link is on its
          way — and an address that has opted out of Academia email is sent nothing at all,
          which this cannot say either way without disclosing a stranger's email preferences to
          whoever typed their address.
        */
        <Banner type="success">
          Check your inbox. We&apos;ve sent a confirmation link to that address.
        </Banner>
      ) : (
        <SignupSection
          subscribePath={props.subscribePath}
          journal={journal}
          relatedJournals={relatedJournals}
          journals={props.journals}
          searchKeywords={props.searchKeywords}
          onConfirmationSent={() => setConfirmationSent(true)}
        />
      )}

      {footerPath && (
        <Footer>
          {/*
            Underlined because it stands on its own line rather than inside a sentence.
            BodyLink is `color: inherit; text-decoration: none` by default, which reads as a
            caption rather than a link when there is no surrounding prose to mark it as one.
          */}
          <BodyLink href={footerPath} underline>
            Manage your Academia.edu notifications
          </BodyLink>
        </Footer>
      )}
    </Page>
  );
}
